/**
 * InSync Analytics Add-in Commands
 * Handles ribbon button actions (Refresh Data, Unlink Formulas)
 */

/* global Office, Excel */

import { loginService } from '../core/auth/loginService';
import { config } from '../utils/config';
import { logAction } from '../utils/actionLog';

type PaneRoute = {
  pane: string;
  mode?: string;
  action?: string;
};

const PENDING_ROUTE_STORAGE_KEY = "insync:pendingPaneRoute";
const LAST_ROUTE_STORAGE_KEY = "insync:lastPaneRoute";

const PANE_ROUTES: Record<string, PaneRoute> = {
  openLoginPane: { pane: "login" },
  openInsertDataPane: { pane: "insertData" },
  openInsertFormulaPane: { pane: "insertFormula" },
  openClosingPricePane: { pane: "stockPrices", mode: "closing" },
  openHistoricalPricesPane: { pane: "stockPrices", mode: "historical" },
  openStockSplitPane: { pane: "stockPrices", mode: "split" },
  openValuationPane: { pane: "stockPrices", mode: "valuation" },
  openModelsPane: { pane: "models" },
  openBrokerRevisionsPane: { pane: "brokerRevisions" },
  openDynamicApiPane: { pane: "dynamicApi" },
  openAuditPane: { pane: "audit" },
  openRefreshPane: { pane: "refresh" },
  openRefreshWorksheetPane: { pane: "refresh", action: "worksheet" },
  openRefreshWorkbookPane: { pane: "refresh", action: "workbook" },
  openRefreshServerPane: { pane: "refresh", action: "server" },
  openUnlinkPane: { pane: "unlink" },
  openUnlinkWorksheetPane: { pane: "unlink", action: "worksheet" },
  openUnlinkWorkbookPane: { pane: "unlink", action: "workbook" },
  openUnlinkSelectionPane: { pane: "unlink", action: "selection" },
  openRelinkPane: { pane: "relink" },
  openRelinkWorksheetPane: { pane: "relink", action: "worksheet" },
  openRelinkSelectionPane: { pane: "relink", action: "selection" },
  openProfilePane: { pane: "profile" },
  openContactPane: { pane: "contact" },
  openLogoutPane: { pane: "logout" },
};

function buildRouteSearch(route: PaneRoute): string {
  const params = new URLSearchParams();
  params.set("pane", route.pane);

  if (route.mode) {
    params.set("mode", route.mode);
  }

  if (route.action) {
    params.set("action", route.action);
  }

  return `?${params.toString()}`;
}

function saveRoute(storageKey: string, route: PaneRoute, label: string) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(route));
  } catch (error) {
    console.warn(`[Commands] Unable to persist ${label} pane route:`, error);
  }
}

function savePendingRoute(route: PaneRoute) {
  saveRoute(PENDING_ROUTE_STORAGE_KEY, route, "pending");
}

function saveLastRoute(route: PaneRoute) {
  saveRoute(LAST_ROUTE_STORAGE_KEY, route, "last");
}

function replaceRouteSearch(search: string) {
  try {
    if (typeof window.history?.replaceState === "function") {
      window.history.replaceState({}, "", `${window.location.pathname}${search}`);
    }
  } catch (error) {
    console.warn('[Commands] Unable to update pane URL:', error);
  }
}

// Resolves once Office.js has initialized. Office invokes ribbon actions after
// this fires, but Office.addin (the shared-runtime API) can lag slightly on the
// first interaction, so commands await this before revealing the pane.
const officeReady = new Promise<void>((resolve) => {
  Office.onReady(() => {
    console.log('[Commands] Office.js is ready ASG 2');
    resolve();
  });
});

/**
 * Reveal the shared-runtime task pane, retrying while the runtime finishes
 * starting up. Fixes the "click 2-3 times before the pane opens" first-click
 * race on desktop (Edge WebView2): on the first click Office.addin.showAsTaskpane
 * can be briefly unavailable or reject, and the old code fell straight to a
 * window.location fallback that reloaded the hidden pane without revealing it.
 */
async function revealTaskpane(search: string): Promise<void> {
  await officeReady;

  // Shared-runtime hosts (desktop WebView2, web): retry showAsTaskpane while the
  // runtime finishes a cold start. Do NOT fall back to window.location here — in
  // a shared runtime that just reloads the hidden pane WITHOUT revealing it,
  // which is what caused the "click 2-3 times" behavior.
  if (typeof Office.addin?.showAsTaskpane === "function") {
    const maxAttempts = 20; // ~5s of retries covers a cold blank-workbook start
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        await Office.addin.showAsTaskpane();
        return;
      } catch (error) {
        console.warn(`[Commands] showAsTaskpane attempt ${attempt + 1} failed:`, error);
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
    }
    return;
  }

  // Only reached on hosts without a shared runtime (no Office.addin API).
  window.location.href = `${window.location.origin}/taskpane.html${search}`;
}

async function openPane(route: PaneRoute, event: Office.AddinCommands.Event) {
  try {
    const search = buildRouteSearch(route);
    savePendingRoute(route);
    saveLastRoute(route);
    replaceRouteSearch(search);
    const navigationEvent = document.createEvent("CustomEvent");
    navigationEvent.initCustomEvent("insync:navigate-pane", false, false, route);
    window.dispatchEvent(navigationEvent);

    // Always reveal the task pane. In a shared runtime the task pane code keeps
    // running (and the pane may simply be hidden) even when it's not visible, so
    // we can't skip showAsTaskpane() based on a heartbeat — doing so would update
    // the hidden pane's route without ever bringing it back into view.
    await revealTaskpane(search);
  } catch (error) {
    console.error('[Commands] Unable to open pane:', route, error);
    try {
      window.location.href = `${window.location.origin}/taskpane.html${buildRouteSearch(route)}`;
    } catch (fallbackError) {
      console.error('[Commands] Unable to navigate to pane fallback:', fallbackError);
    }
  } finally {
    event.completed();
  }
}

function makeOpenPaneCommand(commandName: string) {
  return async (event: Office.AddinCommands.Event) => {
    const route = PANE_ROUTES[commandName];

    if (!route) {
      console.error(`[Commands] No pane route registered for ${commandName}`);
      event.completed();
      return;
    }

    await openPane(route, event);
  };
}

/**
 * Autorun handler fired when a workbook is opened. Its only job is to make Office
 * start the shared runtime at document-open time, so the runtime is already warm
 * when the user first clicks a ribbon button. Without this, the first ribbon
 * click is spent cold-starting the runtime and the task pane only opens on the
 * second click (the "click twice" bug on Edge WebView2 / web).
 */
async function onDocumentOpened(event: Office.AddinCommands.Event) {
  try {
    console.log('[Commands] Document opened — shared runtime pre-warmed');
  } finally {
    event.completed();
  }
}

async function notImplementedCommand(event: Office.AddinCommands.Event) {
  console.log('[Commands] Ribbon command is present but not implemented in phase 1 yet');
  event.completed();
}

async function aboutCommand(event: Office.AddinCommands.Event) {
  console.log('[Commands] Info triggered - InSync Analytics Add-in v1.0.0');
  event.completed();
}

function openExternalUrl(url: string) {
  if (Office.context?.ui?.openBrowserWindow) {
    Office.context.ui.openBrowserWindow(url);
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

async function openInSyncMcpCommand(event: Office.AddinCommands.Event) {
  console.log('[Commands] InSync MCP triggered');

  try {
    logAction('InSyncMcp.Opened', 'Opened InSync MCP in the browser');
    openExternalUrl('https://insyncanalytics.com/contact-us/');
  } catch (error) {
    console.error('[Commands] Unable to open InSync MCP URL:', error);
  } finally {
    event.completed();
  }
}

async function openIstariWebCommand(event: Office.AddinCommands.Event) {
  console.log('[Commands] ISTARI Web triggered');

  try {
    logAction('IstariWeb.Opened', 'Opened ISTARI Web in the browser');
    openExternalUrl(config.ISTARI_BASE_URL);
  } catch (error) {
    console.error('[Commands] Unable to open ISTARI Web URL:', error);
  } finally {
    event.completed();
  }
}

async function logoutCommand(event: Office.AddinCommands.Event) {
  console.log('[Commands] Logout triggered');

  try {
    await loginService.logout();
  } catch (error) {
    console.error('[Commands] Logout failed:', error);
  } finally {
    event.completed();
  }
}

/**
 * Refresh all IAD/IBD formulas in the workbook
 * Triggered by the "Refresh Data" ribbon button
 */
async function refreshData(event: Office.AddinCommands.Event) {
  console.log('[Commands] Refresh Data triggered');
  
  try {
    await Excel.run(async (context) => {
      // Recalculate all formulas
      context.workbook.application.calculate(Excel.CalculationType.fullRebuild);
      await context.sync();
      
      console.log('[Commands] Workbook recalculation complete');
    });
    
    // Show success notification
    if (Office.context?.mailbox?.item?.notificationMessages) {
      const message: Office.NotificationMessageDetails = {
        type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
        message: "Data refresh initiated. Formulas will update shortly.",
        icon: "Icon.80x80",
        persistent: false,
      };
      Office.context.mailbox.item.notificationMessages.replaceAsync("RefreshNotification", message);
    }
  } catch (error) {
    console.error('[Commands] Refresh failed:', error);
  }
  
  // Complete the command
  event.completed();
}

async function refreshWorksheet(event: Office.AddinCommands.Event) {
  console.log('[Commands] Refresh Current Worksheet triggered');
  await refreshData(event);
}

async function refreshWorkbook(event: Office.AddinCommands.Event) {
  console.log('[Commands] Refresh Entire Workbook triggered');
  await refreshData(event);
}

/**
 * Convert all IAD/IBD formulas to static values
 * Triggered by the "Unlink Formulas" ribbon button
 */
async function unlinkFormulas(event: Office.AddinCommands.Event) {
  console.log('[Commands] Unlink Formulas triggered');
  
  try {
    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      
      let convertedCount = 0;
      
      for (const sheet of sheets.items) {
        // Check if this is an IA_Data sheet
        if (sheet.name.includes('_IA_Data')) {
          const usedRange = sheet.getUsedRange();
          usedRange.load("formulas");
          await context.sync();
          
          // Get formulas and convert to values
          const formulas = usedRange.formulas;
          const values: any[][] = [];
          
          for (let row = 0; row < formulas.length; row++) {
            values[row] = [];
            for (let col = 0; col < formulas[row].length; col++) {
              const cell = formulas[row][col];
              // If it's an IAD/IBD formula, we'll keep the formula (it will evaluate)
              // In value mode, we would copy values here
              if (typeof cell === 'string' && cell.includes('IAD_OFJ')) {
                convertedCount++;
              }
              values[row][col] = cell;
            }
          }
          
          console.log(`[Commands] Processed sheet ${sheet.name}, found ${convertedCount} formulas`);
        }
      }
      
      await context.sync();
    });
    
    // Show notification
    if (Office.context?.mailbox?.item?.notificationMessages) {
      const message: Office.NotificationMessageDetails = {
        type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
        message: "Formulas unlinked. All IAD/IBD data is now static.",
        icon: "Icon.80x80",
        persistent: false,
      };
      Office.context.mailbox.item.notificationMessages.replaceAsync("UnlinkNotification", message);
    }
  } catch (error) {
    console.error('[Commands] Unlink failed:', error);
  }
  
  // Complete the command
  event.completed();
}

async function unlinkWorksheet(event: Office.AddinCommands.Event) {
  console.log('[Commands] Unlink Current Worksheet triggered');
  await unlinkFormulas(event);
}

async function unlinkWorkbook(event: Office.AddinCommands.Event) {
  console.log('[Commands] Unlink Entire Workbook triggered');
  await unlinkFormulas(event);
}

async function unlinkSelection(event: Office.AddinCommands.Event) {
  console.log('[Commands] Unlink Selection triggered');
  await unlinkFormulas(event);
}

Office.actions.associate("onDocumentOpened", onDocumentOpened);
Office.actions.associate("refreshData", refreshData);
Office.actions.associate("refreshWorksheet", refreshWorksheet);
Office.actions.associate("refreshWorkbook", refreshWorkbook);
Office.actions.associate("unlinkFormulas", unlinkFormulas);
Office.actions.associate("unlinkWorksheet", unlinkWorksheet);
Office.actions.associate("unlinkWorkbook", unlinkWorkbook);
Office.actions.associate("unlinkSelection", unlinkSelection);
Office.actions.associate("aboutCommand", aboutCommand);
Office.actions.associate("logoutCommand", logoutCommand);
Office.actions.associate("openInSyncMcpCommand", openInSyncMcpCommand);
Office.actions.associate("openIstariWebCommand", openIstariWebCommand);
Office.actions.associate("notImplementedCommand", notImplementedCommand);

Object.keys(PANE_ROUTES).forEach((commandName) => {
  Office.actions.associate(commandName, makeOpenPaneCommand(commandName));
});
